import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Observable, finalize, take } from 'rxjs'
import { TokenizedMessageLoadingService } from '../tokenized-message-loading/tokenized-message-loading.service'
import { KeyingAppEnvironmentConfig } from '../../configuration/keying.app.environment.config'

/**
 * Attachment interface - represents a file attachment for a structure
 */
export interface Attachment {
  id: number
  structureId: number
  name: string
  createdOn: string
  createdById: number
  userName: string
  createdBy: string
}

/**
 * Request interface for creating/uploading new attachments
 */
export interface CreateAttachmentRequest {
  fileName: string
  description?: string
  file: File
}

/**
 * Service for managing file attachments within key structures
 *
 * Provides attachment management functionality including upload, download, 
 * and deletion operations. Uses tokenized loading states for all operations.
 *
 * Key features:
 * - Structure-scoped attachment operations (upload, download, delete)
 * - Tokenized loading states for concurrent operations
 * - Direct API calls without caching for fresh data
 * - File upload with FormData and proper multipart handling
 */
@Injectable({
  providedIn: 'root',
})
export class AttachmentsService {
  private readonly httpClient = inject(HttpClient)
  private readonly tokenizedMessageLoadingService = inject(TokenizedMessageLoadingService)
  private readonly keyingAppEnvironmentConfig = inject(KeyingAppEnvironmentConfig)

  /**
   * Get all attachments for a structure
   *
   * @param structureId The structure ID to get attachments for
   * @returns Observable of attachments array
   *
   * NOTE: Uses /Documents endpoint to match existing API
   */
  getAttachments(structureId: number): Observable<Attachment[]> {
    const token = this.tokenizedMessageLoadingService.startLoading('Loading Attachments', 'load-attachments', 'medium')

    const headers = {
      headers: new HttpHeaders({
        'Content-Type': 'application/allegion.keying.structure.attachment+json',
      }),
    }

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Documents`

    return this.httpClient.get<Attachment[]>(url, headers).pipe(
      take(1),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  /**
   * Upload a new attachment to a structure
   *
   * @param attachmentRequest The file and metadata to upload
   * @param structureId The structure ID to attach the file to
   * @returns Observable of upload result
   */
  uploadAttachment(attachmentRequest: CreateAttachmentRequest, structureId: number): Observable<Attachment> {
    const token = this.tokenizedMessageLoadingService.startLoading(
      'Uploading Attachment',
      'upload-attachment',
      'medium'
    )

    // Create FormData for multipart upload
    const formData = new FormData()
    formData.append('formFile', attachmentRequest.file, attachmentRequest.fileName)
    if (attachmentRequest.description) {
      formData.append('description', attachmentRequest.description)
    }

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Documents`
    return this.httpClient.post<Attachment>(url, formData).pipe(
      take(1),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  /**
   * Delete an attachment from a structure
   *
   * @param attachmentId The attachment ID to delete
   * @param structureId The structure ID that owns the attachment
   * @returns Observable of delete result
   */
  deleteAttachment(attachmentId: number, structureId: number): Observable<void> {
    const token = this.tokenizedMessageLoadingService.startLoading('Deleting Attachment', 'delete-attachment', 'medium')

    const headers = new HttpHeaders().set(
      'Content-Type',
      'application/allegion.keying.delete.structure.attachment+json'
    )
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Documents/${attachmentId}`
    return this.httpClient.delete<void>(url, { headers }).pipe(
      take(1),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  /**
   * Download an attachment file
   *
   * @param attachmentId The attachment ID to download
   * @param structureId The structure ID that owns the attachment
   * @returns Observable of file blob
   */
  downloadAttachment(attachmentId: number, structureId: number): Observable<Blob> {
    const token = this.tokenizedMessageLoadingService.startLoading(
      'Downloading Attachment',
      'download-attachment',
      'medium'
    )

    const headers = new HttpHeaders().set('Content-Type', 'application/allegion.keying.structure.attachment+json')
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Documents/${attachmentId}`
    
    // Return blob for file download
    return this.httpClient.get(url, { headers, responseType: 'blob' }).pipe(
      take(1),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }
}
